import {NumberType, Weekday} from "./api";
import {Dispatch} from "react";
import {OutgoingCallBarring} from "@/api";
import {saveAs} from "file-saver";
import {showNotification} from "@/actions";
import {defineMessages} from "react-intl";
import {ActivationTime} from "./api";
import groupBy from "lodash/groupBy";
import {IntlShape} from "react-intl";
import deepEqual from "deep-equal";
import {ErrorWithContext} from "@dvrt/graphql-widgets";
import {parseContext} from "./utils/ErrorHelper";
import {UNLIMITED} from "./constants";

/// indicates that we have initiated page reloading and pending operations can be ignored
export let reloading = false;

/// reloads the page (and sets the reloading flag)
export function reload(): void {
    reloading = true;
    window.location.reload();
}

/// set the reloading flag, so the pending operations can be clearly closed
export function setReloadingFlag(): void {
    reloading = true;
}

/// returns the keys of the specified object preserving the types
/// Object.keys replacement with proper typing
export function objectKeys<T extends object>(obj: T): (keyof T)[] {
    return Object.keys(obj) as (keyof T)[];
}

export enum WeekdaySorting {
    MONDAY = 1,
    TUESDAY = 2,
    WEDNESDAY = 3,
    THURSDAY = 4,
    FRIDAY = 5,
    SATURDAY = 6,
    SUNDAY = 7,
}

export const MAX_I32 = 2147483647;
export const MAX_I64 = Number.MAX_SAFE_INTEGER;

export const convertCamelToSnakeCase = (s: string): string =>
    s.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export type StringObject = {[s: string]: any};

export function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function convertToCamelCase(key: string): string {
    return key
        .split("_")
        .map((s) => capitalizeFirstLetter(s))
        .join("");
}

function isObject(obj: any): boolean {
    return obj !== null && typeof obj === "object";
}

export function convertKeysToCamelCase(data: Record<string, any>): Record<string, any> {
    const res = {} as Record<string, any>;
    for (const key of Object.keys(data)) {
        const newKey = convertToCamelCase(key);
        const value = data[key];
        res[newKey.charAt(0).toLowerCase() + newKey.slice(1)] = isObject(value)
            ? convertKeysToCamelCase(value as Record<string, any>)
            : value;
    }
    return res;
}

/**
 * Removes the "key" attributes from the COPY of the "obj" parameter
 * @param obj source object, remains untouched
 * @param key keyof obj
 * @return copy of the obj
 */
export function removeAttr<T>(obj: T, key: keyof T): T {
    const res = {...obj};
    if (res[key] !== undefined) delete res[key];
    return res;
}

export function isColor(color: string) {
    const style = new Option().style;
    style.color = color;
    return style.color !== "";
}

export function formatShortNumberForSpeedDial(value: string): string {
    const _number = Number(value);

    return _number > 9 ? `**${_number}` : value;
}

export function areObjectsEqual(first: any, second: any): boolean {
    if (Object.getOwnPropertyNames(first).length !== Object.getOwnPropertyNames(second).length) {
        return false;
    }

    for (const key in first) {
        if (!Object.prototype.hasOwnProperty.call(first, key) || !Object.prototype.hasOwnProperty.call(second, key)) {
            return false;
        }

        if (isObject(first[key]) && isObject(second[key])) {
            if (!areObjectsEqual(first[key], second[key])) {
                return false;
            }
        } else if (first[key] !== second[key]) {
            return false;
        }
    }

    return true;
}

export function hasSameElements(list1: any[], list2: any[]): boolean {
    const set1 = new Set(list1);
    const set2 = new Set(list2);
    if (set1.size !== set2.size) return false;

    for (const item of set1) {
        if (!set2.has(item)) return false;
    }

    return true;
}

export type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Updates an object with new (even deeply nested) data. Mutates the object. Returns the reference to the object.
 **/
export const deepUpdateObject = <T extends object, R extends DeepPartial<T>>(toModify: T, newData: R): T => {
    if (newData === undefined || newData === null) return toModify;

    const mod = toModify as any;
    const newD = newData as any;
    for (const key of Object.keys(newD)) {
        if (mod[key] === undefined || mod[key] === null) {
            mod[key] = structuredClone(newD[key]);
        } else if (Array.isArray(newD[key])) {
            mod[key] = structuredClone(newD[key]);
        } else if (isObject(newD[key])) {
            mod[key] = deepUpdateObject(mod[key], newD[key]);
        } else {
            mod[key] = structuredClone(newD[key]);
        }
    }
    return mod;
};

export const createQueryString = (params: Record<string, any>): string => {
    return Object.keys(params)
        .map((key) => {
            return encodeURIComponent(key) + "=" + encodeURIComponent(params[key]);
        })
        .join("&");
};

export const getKeys = Object.keys as <T extends object>(obj: T) => (keyof T)[];

export function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export const numberTypeTranslations = defineMessages<NumberType>({
    [NumberType.Extension]: {id: "numberType.extension", defaultMessage: "User"},
    [NumberType.Fax]: {id: "numberType.fax", defaultMessage: "Fax"},
    [NumberType.Group]: {id: "numberType.group", defaultMessage: "Call group"},
    [NumberType.GroupInternal]: {id: "numberType.group_internal", defaultMessage: "Pickup group"},
    [NumberType.Gateway]: {id: "numberType.gw", defaultMessage: "Gateway"},
    [NumberType.Ivr]: {id: "numberType.ivr", defaultMessage: "Auto Attendant"},
});

export function createMasterBarrings(
    ...barrings: (OutgoingCallBarring | OutgoingCallBarring[])[]
): OutgoingCallBarring[] {
    const result: OutgoingCallBarring[] = [];

    for (const barringSet of barrings) {
        if (barringSet instanceof Array) {
            result.push(...barringSet);
        } else {
            result.push(barringSet);
        }
    }

    return [...new Set(result)];
}

export function compareCallBarrings(
    lhs: OutgoingCallBarring | OutgoingCallBarring[],
    rhs: OutgoingCallBarring | OutgoingCallBarring[],
): boolean {
    const leftList = lhs instanceof Array ? lhs : [lhs];
    const rightList = rhs instanceof Array ? rhs : [rhs];
    return hasSameElements(leftList, rightList);
}

export function getRandomString(length: number = 20, character_set?: string): string {
    character_set ??= "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
        result += character_set.charAt(Math.floor(Math.random() * character_set.length));
    }
    return result;
}

export function getRandomInt(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min) + min);
}

export const zeroPad = (num: number, places: number = 2) => num.toString().padStart(places, "0");

export function updateObject<T extends object>(target: T, source: Partial<T>): T {
    return Object.assign(target, source);
}

// Similar to String[].join(), it returns a new array with the same type,
// but with separators inserted between each element.
// Array.prototype.insertSeparator = function <T>(separator: T): T[] {
export function insertSeparator<T>(orig: T[], separator: T): T[] {
    const elements: T[] = [];
    orig.forEach((e, idx) => {
        elements.push(e);
        if (idx !== orig.length - 1) {
            elements.push(separator);
        }
    });
    return elements;
}

export enum SaveMediaStatus {
    TOKEN_EXPIRED = "media_download_errors.token_expired",
    AUDIO_FILE_ERROR = "media_download_errors.audio_file_error",
    BAD_REQUEST = "media_download_errors.bad_request",
    OK = "media_download_errors.ok",
}

const SAVE_MEDIA_STATUS_MESSAGES = defineMessages<Exclude<SaveMediaStatus, SaveMediaStatus.OK>>({
    [SaveMediaStatus.TOKEN_EXPIRED]: {
        id: "media_download_errors.token_expired",
        defaultMessage: "To download the file, please refresh the page.",
    },
    [SaveMediaStatus.AUDIO_FILE_ERROR]: {
        id: "media_download_errors.audio_file_error",
        defaultMessage: "Could not download media file",
    },
    [SaveMediaStatus.BAD_REQUEST]: {
        id: "media_download_errors.bad_request",
        defaultMessage: "Could not download media file",
    },
});

type SaveMedia = (
    | {
          filename: string;
          blob: Blob;
          status: SaveMediaStatus.OK;
      }
    | {
          filename: undefined;
          blob: undefined;
          status: SaveMediaStatus.TOKEN_EXPIRED | SaveMediaStatus.AUDIO_FILE_ERROR | SaveMediaStatus.BAD_REQUEST;
      }
) & {status: SaveMediaStatus};

const getFilenameFromContentDisposition = (contentDisposition: string): string => {
    const filenamePart = contentDisposition.split(";").find((part) => part.includes("filename="));
    const filename = filenamePart?.split("filename=")[1]?.replace(/"/g, "");

    return filename ?? "audio.wav";
};

/**
 * Downloads the specified media file from the server into memory, and returns it as a Blob.
 *
 * @param accessToken The access token to use for the request.
 * @param downloadToken The ID of the media file to download.
 * @returns The media file as a Blob or `null` if there is no file.
 */
export async function downloadMedia(accessToken: string, downloadToken: string): Promise<SaveMedia> {
    const res = await fetch(`/api/media/${downloadToken}`, {
        method: "GET",
        headers: {
            authorization: `Bearer ${accessToken}`,
        },
    });

    switch (res.status) {
        case 200: {
            const blob = await res.blob();
            if (blob.size === 0)
                return {
                    filename: undefined,
                    blob: undefined,
                    status: SaveMediaStatus.AUDIO_FILE_ERROR,
                };
            return {
                filename: getFilenameFromContentDisposition(res.headers.get("Content-Disposition") ?? "audio.wav"),
                blob: blob,
                status: SaveMediaStatus.OK,
            };
        }
        case 404:
            return {
                filename: undefined,
                blob: undefined,
                status: SaveMediaStatus.TOKEN_EXPIRED,
            };
        default:
            return {
                filename: undefined,
                blob: undefined,
                status: SaveMediaStatus.BAD_REQUEST,
            };
    }
}

// TODO: fileName should not be provided as the information should be taken from the server.
// (currently the extension is taken from the server, but the name is always 'audio.<ext>')
/**
 * Download the specified media file.
 *
 * @param downloadToken The ID of the media file to download.
 * @param fileName The name which will be used to save the file.
 * If not provided, the name will be taken from the server.
 * @param accessToken The access token to use for the request.
 * @param dispatch
 */
export async function saveAudio(
    downloadToken: string,
    fileName: string | undefined,
    accessToken: string,
    dispatch: Dispatch<any>,
) {
    const {filename: fileNameFromBackend, blob, status} = await downloadMedia(accessToken, downloadToken);

    if (status !== SaveMediaStatus.OK) {
        const d = SAVE_MEDIA_STATUS_MESSAGES[status];
        dispatch(showNotification(d, "error", undefined, 3000));
        return;
    }

    const fileExt = fileNameFromBackend.split(".").pop();
    fileName ??= removeFileExtension(fileNameFromBackend);
    saveAs(blob, `${fileName}.${fileExt}`);
}
export type RecursiveKeyOf<TObj extends object> = {
    [TKey in keyof TObj & (string | number)]: TObj[TKey] extends any[]
        ? `${TKey}`
        : NonNullable<TObj[TKey]> extends object
          ? `${TKey}` | `${TKey}.${RecursiveKeyOf<NonNullable<TObj[TKey]>>}`
          : `${TKey}`;
}[keyof TObj & (string | number)];

type OmitDistributive<T, K extends PropertyKey> = T extends any
    ? T extends object
        ? Id<RecursiveOmit<T, K>>
        : T
    : never;
type Id<T> = NonNullable<unknown> & {[P in keyof T]: T[P]};
export type RecursiveOmit<T, K extends PropertyKey> = Omit<{[P in keyof T]: OmitDistributive<T[P], K>}, K>;

/**
 * Helper type that substitutes all instances of `From` with `To` in the given type `T`.
 *
 * @example
 * SubstituteType<{a: string, b: number}, string, boolean> // {a: boolean, b: number}
 **/
export type SubstituteType<T, From, To> = T extends From
    ? To
    : T extends NonNullable<unknown>
      ? {[K in keyof T]: SubstituteType<T[K], From, To>}
      : T;

export type TypenameOmit<T extends object> = RecursiveOmit<T, "__typename">;

/**
 * Extracts the common properties from two types T and U.
 * Their names and types must match exactly.
 */
export type Intersection<T, U> = {
    [K in keyof T & keyof U]: T[K] extends U[K] ? (U[K] extends T[K] ? T[K] : never) : never;
};

/**
 * Deletes the '__typename' property from the specified object recursively.
 *
 * @param obj The object to remove the '__typename' property from.
 * @returns The cleaned object.
 */
export const omitTypenames = <T extends object>(obj: T): TypenameOmit<T> => {
    if (obj && typeof obj === "object") {
        const newObj = structuredClone(obj) as any;
        Object.keys(newObj).forEach((key) => {
            if (isObject(newObj[key])) {
                newObj[key] = omitTypenames(newObj[key]);
            } else if (Array.isArray(newObj[key])) {
                newObj[key] = newObj[key].map((item: any) => omitTypenames(item));
            } else if (key === "__typename") {
                delete newObj[key];
            }
        });
        return newObj as TypenameOmit<T>;
        //try {
        //    const newObj = structuredClone(obj) as any;
        //    Object.keys(newObj).forEach((key) => {
        //        if (isObject(newObj[key])) {
        //            newObj[key] = omitTypenames(newObj[key]);
        //        } else if (Array.isArray(newObj[key])) {
        //            newObj[key] = newObj[key].map((item: any) => omitTypenames(item));
        //        } else if (key === "__typename") {
        //            delete newObj[key];
        //        }
        //    });
        //    return newObj as TypenameOmit<T>;
        //} catch {
        //    console.debug("Object could not be cloned, returning original object", obj);
        //    return obj as TypenameOmit<T>;
        //}
    }
    return obj as TypenameOmit<T>;
};

/**
 * Returns a default orderer function for the specified field type.
 **/
const getDefaultComparator = <T>(fieldType: string) => {
    switch (fieldType) {
        case "string":
            return (isDesc: boolean, field: keyof T) => (a: T, b: T) =>
                (a[field] as string).localeCompare(b[field] as string) * (isDesc ? -1 : 1);
        case "number":
            return (isDesc: boolean, field: keyof T) => (a: T, b: T) =>
                ((a[field] as number) - (b[field] as number)) * (isDesc ? -1 : 1);
        default:
            return undefined;
    }
};

export type LooseAutocomplete<T extends string> = T | Omit<string, T>;

/**
 * In TypeScript, keys of an object can be either string or number.
 * This type is used to specify that the keys are only string.
 * This way, we can use the keys for functions and other types that only accept string.
 **/
export type KeyString<T> = Extract<keyof T, string>;

/**
 * Indicating that the direction of the sort is descending can be done by
 * adding '~' to the beginning of the field name.
 * This type is used for type check whether the specified string
 * is a valid field name, even if it is a descending sort.
 **/
export type OrderField<T> = KeyString<T> | `~${KeyString<T>}`;

/**
 * Function that returns a comparator function with the extra 'isDescending' parameter
 * to consider the direction of the sort.
 **/
export type OrdererFunction<T> = (isDescending: boolean) => (a: T, b: T) => number;

/**
 * Sorts the specified array of objects by the specified order. The first generic is
 * the type of the object the array contains, the second is for actions for that object / record.
 * The function mutates the array.
 *
 * @param records The array of objects to sort.
 * @param order The order to sort the array by. Only its first element is used.
 * @param defaultOrder The default order to use if the specified order is empty.
 * @param customOrderers A map of custom orderers for specific fields.
 **/
export function sortRecords<T, U>(
    records: T[],
    orders: (OrderField<T> | OrderField<U>)[],
    defaultOrder: OrderField<T> | OrderField<U> | undefined,
    customOrderers?: Record<KeyString<T> | string, OrdererFunction<T>>,
): T[] {
    const result = [...records];
    if (!orders.length && defaultOrder === undefined) return result;
    if (result.length === 0) return result;

    const orderField = orders[0] ?? defaultOrder;
    if (orderField === undefined) return result;
    const isDesc = orderField.startsWith("~");
    const fieldName = orderField.replace("~", "") as KeyString<T>;
    const fieldType = typeof (result[0] as any)[fieldName];

    if (customOrderers?.[fieldName]) {
        result.sort(customOrderers[fieldName](isDesc));
        return result;
    }

    const comparatorFactory = getDefaultComparator<T>(fieldType);

    if (!comparatorFactory) return result;

    const comparator = comparatorFactory(isDesc, fieldName);

    return result.sort(comparator);
}

/**
 * Creates a reordered copy of the array by moving the 'from' element to the 'to' index,
 * while pushing every other element to the left or right.
 *
 * @param array The array to reorder.
 * @param from The index of the element to move.
 * @param to The index to move the element to.
 * @returns The reordered array.
 *
 * @example
 * const array = [0, 1, 2, 3, 4, 5];
 * const result = moveItemsInArray(array, 1, 4);
 * // result = [0, 2, 3, 4, 1, 5]
 * */
export const moveItemsInArray = <T>(array: T[], from: number, to: number): T[] => {
    const result = structuredClone(array);
    if (from === to) return result;

    const item = result.splice(from, 1)[0]!;
    result.splice(to, 0, item);

    return result;
};

/**
 * Creates a reordered copy of the array by swapping the 'from' element with the 'to' element,
 * while keeping every other element intact.
 *
 * @param array The array to reorder.
 * @param from The index of the element to swap.
 * @param to The index to swap the element with.
 * @returns The reordered array.
 *
 * @example
 * const array = [0, 1, 2, 3, 4, 5];
 * const result = swapItemsInArray(array, 1, 4);
 * // result = [0, 4, 2, 3, 1, 5]
 * */
export const swapItemsInArray = <T>(array: T[], from: number, to: number): T[] => {
    const result = structuredClone(array);
    if (from === to) return result;

    const item = result.splice(from, 1, result[to]!)[0]!;
    result.splice(to, 1, item);

    return result;
};

/// removes the last dot and everything after it from the string
export function removeFileExtension(input: string): string {
    const index = input.lastIndexOf(".");
    return index > 0 ? input.substring(0, index) : input;
}

export function groupActivationDays(activationTimeList: ActivationTime[]) {
    const groupedTimes = [];

    for (const [, value] of Object.entries(groupBy(activationTimeList, (row) => row.times))) {
        const [first] = value;
        if (!first) continue;

        const times = first.times;
        let weekdays: Weekday[] = [];
        for (const day of value) {
            weekdays = [...weekdays, ...day.weekdays.filter((weekday): weekday is Weekday => weekday !== null)];
        }
        groupedTimes.push({weekdays, times});
    }
    return groupedTimes;
}

export function sortActivationTimes(activationTimeList: ActivationTime) {
    activationTimeList.times.sort((a, b) => {
        return (
            Date.parse("1970/01/01 " + a.slice(0, -2) + " " + a.slice(-2)) -
            Date.parse("1970/01/01 " + b.slice(0, -2) + " " + b.slice(-2))
        );
    });
    return activationTimeList;
}

export function sortActivationDays(activationTimeList: ActivationTime[]) {
    activationTimeList.sort((a, b) => {
        const aWeekday = a.weekdays[0];
        const bWeekday = b.weekdays[0];

        if (aWeekday == null) return bWeekday == null ? 0 : 1;
        if (bWeekday == null) return -1;
        return WeekdaySorting[aWeekday] > WeekdaySorting[bWeekday] ? 1 : -1;
    });
    return activationTimeList;
}

type ProfileActivationTime = {
    activationTime: string;
    weekday: Weekday;
};

export function activationTimePreparer(activationTimes: ProfileActivationTime[]) {
    let initialActivationTimes: ActivationTime[] = [
        {weekdays: [Weekday.Monday], times: []},
        {weekdays: [Weekday.Tuesday], times: []},
        {weekdays: [Weekday.Wednesday], times: []},
        {weekdays: [Weekday.Thursday], times: []},
        {weekdays: [Weekday.Friday], times: []},
        {weekdays: [Weekday.Saturday], times: []},
        {weekdays: [Weekday.Sunday], times: []},
    ];

    for (const activationTime of activationTimes) {
        const weekday = initialActivationTimes.find((time) => time.weekdays.includes(activationTime.weekday));
        if (!weekday) throw new Error(`Unsupported activation weekday: ${activationTime.weekday}`);
        weekday.times.push(activationTime.activationTime);
    }

    initialActivationTimes = groupActivationDays(initialActivationTimes);

    return initialActivationTimes;
}

/**
 * Get the series of buttons that lead to the specified IVR or submenu.
 *
 * @param ivrName The name of the IVR or submenu.
 * @param lastButton The last button of the series.
 *
 * @returns The series of buttons that lead to the specified IVR or submenu.
 **/
export const getIvrButtonPath = (ivrName: string, lastButton: number): number[] =>
    ivrName.search("mainMenu") === -1
        ? [lastButton]
        : ivrName.replace("mainMenu_", "").split("_").map(Number).concat([lastButton]);

/**
 * Formats a duration in milliseconds to a human-readable format with translations.
 * @param milliseconds The duration in milliseconds.
 * @param intl The intl object for translations.
 * @param fullFormat Whether to include hours and minutes in the result when they are 0.
 * @returns The formatted duration.
 **/
export const formatDuration = (milliseconds: number, intl: IntlShape, fullFormat: boolean = false): string => {
    const date = new Date(milliseconds);

    const formatUnit = (value: number, unit: string): string => `${value}${unit}`;

    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();

    let resultFormat = "";

    if (hours > 0) {
        resultFormat = `${formatUnit(hours, intl.formatMessage({id: "time.hours_short", defaultMessage: "h"}))}`;
    }

    if (minutes > 0) {
        resultFormat = `${resultFormat} ${formatUnit(
            minutes,
            intl.formatMessage({id: "time.minutes_short", defaultMessage: "m"}),
        )}`;
    }

    if (seconds > 0 || (fullFormat && hours === 0 && minutes === 0)) {
        resultFormat = `${resultFormat} ${formatUnit(
            seconds,
            intl.formatMessage({id: "time.seconds_short", defaultMessage: "s"}),
        )}`;
    }

    return resultFormat;
};

/** Returns the enum key by its value */
export const getEnumByValue = <T extends {[key: string]: string}>(
    enumType: T,
    value: string,
): T[keyof T] | undefined => {
    const values = Object.values(enumType);
    const index = values.indexOf(value);
    return index === -1 ? undefined : (values[index] as unknown as T[keyof T]);
};

type PruneDefaultsOptions<T extends object> = {
    /** Keys to keep regardless of their values. */
    keepKeys?: RecursiveKeyOf<T>[];

    /** Options for handling empty string values. */
    emptyStringPolicy?: PruneDefaultsEmptyStringPolicy<T>;
};

type PruneDefaultsEmptyStringPolicy<T extends object> = {
    /**
     * Which keys should be set to null if their value is an empty string.
     * If set to "all", all empty string values will be set to null.
     * If set to an array of keys, only those keys will be set to null.
     * If not set, empty string values will be kept as is.
     **/
    nullify?: "all" | RecursiveKeyOf<T>[];
};

/**
 * Deeply removed identical properties from the object compared to the defaults object.
 * */
export function pruneDefaults<T extends Record<string, any>>(
    obj: T,
    defaults: T,
    options?: PruneDefaultsOptions<T>,
): Partial<T> {
    const result: Partial<T> = {};
    const keepKeys = options?.keepKeys || [];
    const emptyStringPolicy = options?.emptyStringPolicy || {nullify: "all"};

    const handleEmptyString = (key: keyof T, value: string) => {
        if (emptyStringPolicy.nullify === "all" || emptyStringPolicy.nullify?.includes(key as any)) {
            result[key] = null as T[typeof key];
        } else {
            result[key] = value as any;
        }
    };

    for (const key in obj) {
        // keys to keep always
        const value = obj[key];
        const defaultValue = defaults[key];
        if (keepKeys.includes(key as any)) {
            if (value === "") {
                handleEmptyString(key, value);
            } else {
                result[key] = value;
            }

            continue;
        }

        if (value === "") {
            // Unchanged empty strings are defaults too. Sending them can also
            // trip backend validation for feature-gated fields hidden by a form.
            if (value !== defaultValue) handleEmptyString(key, value);
            continue;
        }

        if (value === undefined) continue; // skip undefined
        if ((value as any) instanceof File) {
            result[key] = value; // always keep File objects
            continue;
        }

        if (value !== null && typeof value === "object" && !Array.isArray(value)) {
            const filterDeepKeys = (keys: string[] | undefined): string[] | undefined =>
                keys?.filter((k) => k.toString().startsWith(`${key}.`)).map((k) => k.toString().replace(`${key}.`, ""));
            const deepOptions: PruneDefaultsOptions<any> = {
                keepKeys: options?.keepKeys ? filterDeepKeys(options.keepKeys as string[]) : undefined,
                emptyStringPolicy: {
                    nullify: emptyStringPolicy.nullify === "all" ? "all" : filterDeepKeys(emptyStringPolicy.nullify),
                },
            };

            // recursively prune objects
            // do not pass keep keys, as they are only for the root level
            const pruned = pruneDefaults(value, (defaultValue || {}) as any, deepOptions);

            // if there are any properties left, keep the object
            if (Object.keys(pruned).length > 0) {
                result[key] = pruned as T[typeof key];
            }
        } else if (Array.isArray(value)) {
            // compare each element of the array, and if not identical, keep the whole array
            if (!deepEqual(value, defaultValue)) {
                result[key] = value;
            }
        } else {
            // primitive comparison
            if (value !== defaultValue) {
                result[key] = value;
            }
        }
    }

    return result;
}

interface APIError {
    type: string;
    context?: string | null;
}

export const errorToErrorWithContext = (error: APIError | null | undefined): ErrorWithContext =>
    error
        ? {
              type: error.type,
              context: error.context ? parseContext(error.context) : {},
          }
        : (error as unknown as ErrorWithContext);

/**
 * Checks if a value is considered unlimited.
 *
 * Possible unlimited values are: null, undefined, or UNLIMITED number constant.
 **/
export const isUnlimited = (value: number | null | undefined): boolean =>
    value === null || value === undefined || value === UNLIMITED;

/**
 * If the given number is considered unlimited, returns the UNLIMITED number constant,
 * otherwise just returns the given value as is.
 *
 * @see isUnlimited for what is considered unlimited.
 **/
export const unlimitedToNumber = (value: number | null | undefined): number =>
    value === null || value === undefined || value === UNLIMITED ? UNLIMITED : value;
